import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, Modal, Form, Input, Select, InputNumber, Tag, DatePicker, Divider, Card, Row, Col } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, SearchOutlined, ClearOutlined, FilterOutlined } from '@ant-design/icons';
import { unidadeService, tipoUnidadeService, tipoContratoService } from '../services/api';
import { message } from 'antd';
import dayjs from 'dayjs';
import { formatCNPJ, onlyDigits } from '../utils/documento';

const UnidadesList = () => {
  const [unidades, setUnidades] = useState([]);
  const [unidadesFiltradas, setUnidadesFiltradas] = useState([]);
  const [tiposUnidade, setTiposUnidade] = useState([]);
  const [tiposContrato, setTiposContrato] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [mostrarFiltros, setMostrarFiltros] = useState(true);
  const [tipoContratoSelecionado, setTipoContratoSelecionado] = useState(null);
  const [valorParcela, setValorParcela] = useState(0);
  const [form] = Form.useForm();
  const [filtros, setFiltros] = useState({
    busca_geral: '',
    id_tipo_unidades: null,
    id_tipo_contrato: null,
  });

  const compareText = (a, b) =>
    (a || '').toString().localeCompare((b || '').toString(), 'pt-BR', { sensitivity: 'base' });

  useEffect(() => {
    fetchUnidades();
    fetchTiposUnidade();
    fetchTiposContrato();
  }, []);

  const aplicarFiltros = useCallback(() => {
    let resultado = [...unidades];

    if (filtros.id_tipo_unidades) {
      resultado = resultado.filter(u => u.id_tipo_unidades === filtros.id_tipo_unidades);
    }

    if (filtros.id_tipo_contrato) {
      resultado = resultado.filter(u => u.id_tipo_contrato === filtros.id_tipo_contrato);
    }

    if (filtros.busca_geral) {
      const termo = filtros.busca_geral.toLowerCase();
      resultado = resultado.filter(u => {
        const nomeFantasia = u.nome_fantasia ? u.nome_fantasia.toLowerCase() : '';
        const razaoSocial = u.razao_social ? u.razao_social.toLowerCase() : '';
        const cnpj = u.cnpj ? u.cnpj : '';
        return nomeFantasia.includes(termo) || razaoSocial.includes(termo) || cnpj.includes(termo);
      });
    }

    setUnidadesFiltradas(resultado);
  }, [unidades, filtros]);

  useEffect(() => {
    aplicarFiltros();
  }, [aplicarFiltros]);

  const handleFiltroChange = (campo, valor) => {
    setFiltros(prev => ({ ...prev, [campo]: valor }));
  };

  const limparFiltros = () => {
    setFiltros({ busca_geral: '', id_tipo_unidades: null, id_tipo_contrato: null });
    message.success('Filtros limpos');
  };

  const fetchUnidades = async () => {
    setLoading(true);
    try {
      const response = await unidadeService.list();
      setUnidades(response.data);
    } catch (error) {
      message.error('Erro ao carregar unidades');
    }
    setLoading(false);
  };

  const fetchTiposUnidade = async () => {
    try {
      const response = await tipoUnidadeService.list();
      setTiposUnidade(response.data);
    } catch (error) {
      console.error('Erro ao carregar tipos de unidades');
    }
  };

  const fetchTiposContrato = async () => {
    try {
      const response = await tipoContratoService.list();
      setTiposContrato(response.data);
    } catch (error) {
      console.error('Erro ao carregar tipos de contrato');
    }
  };

  const handleAdd = () => {
    form.resetFields();
    setEditingId(null);
    setTipoContratoSelecionado(null);
    setValorParcela(0);
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingId(record.id);
    const formData = {
      ...record,
      inicio_contrato: record.inicio_contrato ? dayjs(record.inicio_contrato) : null,
      vigencia_contrato: record.vigencia_contrato ? dayjs(record.vigencia_contrato) : null,
    };
    form.setFieldsValue(formData);
    
    // Buscar o tipo de contrato selecionado
    if (record.id_tipo_contrato) {
      const tipoContrato = tiposContrato.find(t => t.id === record.id_tipo_contrato);
      setTipoContratoSelecionado(tipoContrato?.modelo_de_repasse || null);
    }
    
    // Calcular valor da parcela se existir
    if (record.valor_a_receber && record.qtd_parcelas) {
      setValorParcela(record.valor_a_receber / record.qtd_parcelas);
    }
    
    setIsModalVisible(true);
  };

  const calcularValorParcela = () => {
    const valorAReceber = form.getFieldValue('valor_a_receber');
    const qtdParcelas = form.getFieldValue('qtd_parcelas');
    
    if (valorAReceber && qtdParcelas && qtdParcelas > 0) {
      setValorParcela(valorAReceber / qtdParcelas);
    } else {
      setValorParcela(0);
    }
  };

  const handleDelete = async (id) => {
    Modal.confirm({
      title: 'Confirmação',
      content: 'Deseja excluir esta unidade?',
      okText: 'Sim',
      cancelText: 'Não',
      onOk: async () => {
        try {
          await unidadeService.delete(id);
          message.success('Unidade excluída com sucesso');
          fetchUnidades();
        } catch (error) {
          message.error('Erro ao excluir unidade');
        }
      },
    });
  };

  const handleSubmit = async (values) => {
    try {
      const submitData = {
        ...values,
        cnpj: onlyDigits(values.cnpj).slice(0, 14),
        inicio_contrato: values.inicio_contrato ? values.inicio_contrato.format('YYYY-MM-DD') : null,
        vigencia_contrato: values.vigencia_contrato ? values.vigencia_contrato.format('YYYY-MM-DD') : null,
      };

      if (editingId) {
        await unidadeService.update(editingId, submitData);
        message.success('Unidade atualizada com sucesso');
      } else {
        await unidadeService.create(submitData);
        message.success('Unidade criada com sucesso');
      }
      setIsModalVisible(false);
      fetchUnidades();
    } catch (error) {
      message.error('Erro ao salvar unidade');
      console.error(error);
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 50,
      sorter: (a, b) => (Number(a?.id) || 0) - (Number(b?.id) || 0),
    },
    {
      title: 'Nome Fantasia',
      dataIndex: 'nome_fantasia',
      key: 'nome_fantasia',
      sorter: (a, b) => compareText(a.nome_fantasia, b.nome_fantasia),
    },
    {
      title: 'CNPJ',
      dataIndex: 'cnpj',
      key: 'cnpj',
      render: (cnpj) => formatCNPJ(cnpj),
    },
    {
      title: 'Contato',
      dataIndex: 'contato_nome',
      key: 'contato_nome',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'WhatsApp',
      dataIndex: 'whatsapp',
      key: 'whatsapp',
    },
    {
      title: 'Cidade',
      dataIndex: 'cidade',
      key: 'cidade',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (text) => (
        <Tag color={text === 'ATIVA' ? 'green' : 'red'}>{text}</Tag>
      ),
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          />
          <Button
            icon={<DeleteOutlined />}
            danger
            onClick={() => handleDelete(record.id)}
            size="small"
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Painel de Filtros */}
      <Card 
        title={
          <Space>
            <FilterOutlined />
            Filtros de Busca
            <span style={{ fontSize: '12px', fontWeight: 'normal', color: '#888' }}>
              ({unidadesFiltradas.length} de {unidades.length} unidades)
            </span>
          </Space>
        }
        extra={
          <Button type="text" size="small" onClick={() => setMostrarFiltros(!mostrarFiltros)}>
            {mostrarFiltros ? 'Ocultar' : 'Mostrar'}
          </Button>
        }
        style={{ marginBottom: 16 }}
      >
        {mostrarFiltros && (
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>Busca Geral</div>
              <Input
                placeholder="Pesquisar por nome, razão social, CNPJ..."
                prefix={<SearchOutlined />}
                value={filtros.busca_geral}
                onChange={(e) => handleFiltroChange('busca_geral', e.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>Tipo de Unidade</div>
              <Select
                placeholder="Todos os tipos"
                value={filtros.id_tipo_unidades}
                onChange={(value) => handleFiltroChange('id_tipo_unidades', value)}
                allowClear
                style={{ width: '100%' }}
              >
                {tiposUnidade.map(t => (
                  <Select.Option key={t.id} value={t.id}>{t.nome_unidade}</Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>Tipo de Contrato</div>
              <Select
                placeholder="Todos os contratos"
                value={filtros.id_tipo_contrato}
                onChange={(value) => handleFiltroChange('id_tipo_contrato', value)}
                allowClear
                style={{ width: '100%' }}
              >
                {tiposContrato.map(t => (
                  <Select.Option key={t.id} value={t.id}>{t.tipo_contrato}</Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>&nbsp;</div>
              <Button icon={<ClearOutlined />} onClick={limparFiltros} block>
                Limpar Filtros
              </Button>
            </Col>
          </Row>
        )}
      </Card>

      <div style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Nova Unidade
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={unidadesFiltradas}
        loading={loading}
        rowKey="id"
        pagination={{ 
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total de ${total} unidades`,
          pageSizeOptions: ['10', '20', '50', '100']
        }}
      />

      <Modal
        title={editingId ? 'Editar Unidade' : 'Nova Unidade'}
        visible={isModalVisible}
        onOk={() => form.submit()}
        onCancel={() => setIsModalVisible(false)}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          {/* Dados Básicos */}
          <Divider>Dados Básicos</Divider>
          
          <Form.Item
            name="id_tipo_unidade"
            label="Tipo de Unidade"
            rules={[{ required: true, message: 'Selecione o tipo' }]}
          >
            <Select placeholder="Selecione o tipo">
              {tiposUnidade.map((tipo) => (
                <Select.Option key={tipo.id} value={tipo.id}>
                  {tipo.nome_unidade}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="cnpj"
            label="CNPJ"
            rules={[{ required: true, message: 'CNPJ é obrigatório' }]}
            getValueFromEvent={(e) => onlyDigits(e?.target?.value).slice(0, 14)}
            getValueProps={(value) => ({ value: formatCNPJ(value) })}
          >
            <Input placeholder="00.000.000/0000-00" />
          </Form.Item>

          <Form.Item
            name="nome_fantasia"
            label="Nome Fantasia"
            rules={[{ required: true, message: 'Nome fantasia é obrigatório' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="razao_social"
            label="Razão Social"
            rules={[{ required: true, message: 'Razão social é obrigatória' }]}
          >
            <Input />
          </Form.Item>

          {/* Contato */}
          <Divider>Dados de Contato</Divider>

          <Form.Item
            name="contato_nome"
            label="Nome do Contato"
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
          >
            <Input type="email" />
          </Form.Item>

          <Form.Item
            name="whatsapp"
            label="WhatsApp"
          >
            <Input placeholder="(11) 99999-9999" />
          </Form.Item>

          {/* Endereço */}
          <Divider>Endereço</Divider>

          <Form.Item
            name="endereco"
            label="Endereço"
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="numero"
            label="Número"
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="complemento"
            label="Complemento"
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="bairro"
            label="Bairro"
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="cep"
            label="CEP"
          >
            <Input placeholder="00000-000" />
          </Form.Item>

          <Form.Item
            name="cidade"
            label="Cidade"
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="uf"
            label="UF"
          >
            <Select placeholder="Selecione o estado">
              <Select.Option value="AC">AC</Select.Option>
              <Select.Option value="AL">AL</Select.Option>
              <Select.Option value="AP">AP</Select.Option>
              <Select.Option value="AM">AM</Select.Option>
              <Select.Option value="BA">BA</Select.Option>
              <Select.Option value="CE">CE</Select.Option>
              <Select.Option value="DF">DF</Select.Option>
              <Select.Option value="ES">ES</Select.Option>
              <Select.Option value="GO">GO</Select.Option>
              <Select.Option value="MA">MA</Select.Option>
              <Select.Option value="MT">MT</Select.Option>
              <Select.Option value="MS">MS</Select.Option>
              <Select.Option value="MG">MG</Select.Option>
              <Select.Option value="PA">PA</Select.Option>
              <Select.Option value="PB">PB</Select.Option>
              <Select.Option value="PR">PR</Select.Option>
              <Select.Option value="PE">PE</Select.Option>
              <Select.Option value="PI">PI</Select.Option>
              <Select.Option value="RJ">RJ</Select.Option>
              <Select.Option value="RN">RN</Select.Option>
              <Select.Option value="RS">RS</Select.Option>
              <Select.Option value="RO">RO</Select.Option>
              <Select.Option value="RR">RR</Select.Option>
              <Select.Option value="SC">SC</Select.Option>
              <Select.Option value="SP">SP</Select.Option>
              <Select.Option value="SE">SE</Select.Option>
              <Select.Option value="TO">TO</Select.Option>
            </Select>
          </Form.Item>

          {/* Contrato */}
          <Divider>Dados do Contrato</Divider>

          <Form.Item
            name="inicio_contrato"
            label="Data de Início do Contrato"
          >
            <DatePicker format="DD/MM/YYYY" />
          </Form.Item>

          <Form.Item
            name="vigencia_contrato"
            label="Vigência do Contrato"
          >
            <DatePicker format="DD/MM/YYYY" />
          </Form.Item>

          <Form.Item
            name="id_tipo_contrato"
            label="Tipo de Contrato"
          >
            <Select 
              placeholder="Selecione o tipo de contrato"
              onChange={(value) => {
                const tipoContrato = tiposContrato.find(t => t.id === value);
                setTipoContratoSelecionado(tipoContrato?.modelo_de_repasse || null);
                // Limpar campos de repasse ao trocar tipo
                form.setFieldsValue({ repasse_percentual: null, valor_a_receber: null });
              }}
            >
              {tiposContrato.map((tipo) => (
                <Select.Option key={tipo.id} value={tipo.id}>
                  {tipo.tipo_contrato}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {/* Repasse */}
          <Divider>Dados de Repasse</Divider>

          <Form.Item
            name="repasse_tipo"
            label="Tipo de Repasse"
          >
            <Select placeholder="Selecione o tipo">
              <Select.Option value="MENSAL">Mensal</Select.Option>
              <Select.Option value="TRIMESTRAL">Trimestral</Select.Option>
              <Select.Option value="SEMESTRAL">Semestral</Select.Option>
              <Select.Option value="ANUAL">Anual</Select.Option>
            </Select>
          </Form.Item>

          {/* Campo para Contrato GLOBAL - Percentual */}
          {tipoContratoSelecionado === 'GLOBAL' && (
            <Form.Item
              name="repasse_percentual"
              label="Percentual de Repasse (%)"
              help="Percentual da receita total dos alunos que será repassado mensalmente"
            >
              <InputNumber
                min={0}
                max={100}
                step={0.1}
                precision={2}
                style={{ width: '100%' }}
                placeholder="Ex: 10.5 para 10.5%"
                addonAfter="%"
              />
            </Form.Item>
          )}

          {/* Campo para Contrato PARCIAL - Valor Fixo */}
          {tipoContratoSelecionado === 'PARCIAL' && (
            <>
              <Form.Item
                name="valor_a_receber"
                label="Valor Total a Receber (R$)"
                help="Valor total anual que a escola deve pagar"
              >
                <InputNumber
                  prefix="R$"
                  min={0}
                  step={0.01}
                  precision={2}
                  style={{ width: '100%' }}
                  placeholder="Ex: 24000.00"
                  onChange={calcularValorParcela}
                />
              </Form.Item>

              <Form.Item
                name="qtd_parcelas"
                label="Quantidade de Parcelas"
                help="Número de parcelas para dividir o valor total"
              >
                <InputNumber
                  min={1}
                  max={12}
                  style={{ width: '100%' }}
                  placeholder="Ex: 12"
                  onChange={calcularValorParcela}
                />
              </Form.Item>

              {valorParcela > 0 && (
                <div style={{ 
                  padding: '12px', 
                  backgroundColor: '#e6f7ff', 
                  border: '1px solid #91d5ff',
                  borderRadius: '4px',
                  marginBottom: '16px'
                }}>
                  <strong>Valor da Parcela:</strong> R$ {valorParcela.toFixed(2)}
                </div>
              )}
            </>
          )}

          {/* Status e Observações */}
          <Divider>Status e Observações</Divider>

          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: 'Status é obrigatório' }]}
          >
            <Select>
              <Select.Option value="ATIVA">Ativa</Select.Option>
              <Select.Option value="INATIVA">Inativa</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="obs"
            label="Observações"
          >
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UnidadesList;
